import { useCallback, useRef } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  InputGroup,
  useToast,
  Text,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import { ModalFormData, modalFormSchema } from './schemas';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { ImageUpload } from '@/components/ImageUpload';
import { usePostPhotoGalleryMutation } from '@/services/queries/photo-gallery';
import { PostPhotoGalleryRequest } from '@/services/request/photo-gallery';
import { useTranslation } from 'react-i18next';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';

export interface PhotoGalleryCreateModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  onClose: () => void;
  onDone?: () => void;
}

export const PhotoGalleryCreateModal = ({ isOpen, onClose, onDone }: PhotoGalleryCreateModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const postItemMutation = usePostPhotoGalleryMutation();

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
    clearErrors,
    reset,
    control,
  } = useForm<ModalFormData>({
    defaultValues: {
      title: '',
      description: '',
      file: undefined,
    },
    resolver: zodResolver(modalFormSchema),
  });

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      const payload: PostPhotoGalleryRequest = {
        ...(data.team_uuid ? { team_uuid: data.team_uuid } : {}),
        title: data.title,
        description: data.description,
        credit: data.credit,
        image: data.file,
      };

      console.log(payload);

      postItemMutation.mutate(payload, {
        onSuccess: () => {
          onDone?.();
          reset();
          toast({
            status: 'success',
            title: t('pages.application_history_gallery.components.photo_gallery_create_modal.photo_gallery_create_success'),
          });
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title:
              (resp && resp.message) ||
              t('pages.application_history_gallery.components.photo_gallery_create_modal.photo_gallery_create_error'),
          });
        },
      });
    },
    [onDone, postItemMutation, reset, toast, t],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_history_gallery.components.photo_gallery_create_modal.photo_gallery_create_title')}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
              control={control}
              name='team_uuid'
              prefetchOnOpen
              label={t('pages.application_history_gallery.components.photo_gallery_create_modal.photo_gallery_create_team_label')}
              placeholder={t(
                'pages.application_history_gallery.components.photo_gallery_create_modal.photo_gallery_create_team_label',
              )}
              showFlag
              width='100%'
              usePortal={false}
              error={errors?.team_uuid?.message as string | undefined}
            />
            <InputField
              withoutFloating
              label={t(
                'pages.application_history_gallery.components.photo_gallery_create_modal.photo_gallery_create_title_label',
              )}
              {...register('title')}
              helperText={t(
                'pages.application_history_gallery.components.photo_gallery_create_modal.photo_gallery_create_title_helper',
              )}
              maxLength={200}
              rootProps={{ isInvalid: !!errors?.title, errorMessage: errors?.title?.message }}
            />

            <InputField
              withoutFloating
              label={t(
                'pages.application_history_gallery.components.photo_gallery_create_modal.photo_gallery_create_description_label',
              )}
              {...register('description')}
              helperText={t(
                'pages.application_history_gallery.components.photo_gallery_create_modal.photo_gallery_create_description_helper',
              )}
              maxLength={200}
              rootProps={{ isInvalid: !!errors?.description, errorMessage: errors?.description?.message }}
            />

            <InputField
              withoutFloating
              label={t(
                'pages.application_history_gallery.components.photo_gallery_create_modal.photo_gallery_create_credit_label',
              )}
              {...register('credit')}
              maxLength={50}
              rootProps={{ isInvalid: !!errors?.credit, errorMessage: errors?.credit?.message }}
            />

            <InputGroup display='flex' flexDirection='column' width='100%'>
              <ImageUpload
                label={t(
                  'pages.application_history_gallery.components.photo_gallery_create_modal.photo_gallery_create_image_label',
                )}
                imageURL={''}
                onFileChange={file => setValue('file', file)}
                helpText={t(
                  'pages.application_history_gallery.components.photo_gallery_create_modal.photo_gallery_create_image_helper',
                )}
                error={errors?.file?.message?.toString()}
              />
            </InputGroup>
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent={'space-between'} gap='kilo'>
          <Flex w='100%' justifyContent={'space-between'} alignItems='center' gap={2} paddingLeft={6}>
            <Flex w={'100%'} justifyContent='end' gap={5}>
              <Button variant='outlined' color='gray.600' onClick={onClose}>
                {t('pages.application_history_gallery.components.photo_gallery_create_modal.photo_gallery_create_cancel')}
              </Button>
              <Button onClick={handleConfirm} bg='green.500' paddingX={10}>
                {t('pages.application_history_gallery.components.photo_gallery_create_modal.photo_gallery_create_create')}
              </Button>
            </Flex>
          </Flex>
        </ModalFooter>
      </ModalContent>
      <CenteredSpinner isLoading={postItemMutation.isLoading} />
    </Modal>
  );
};
